import React, { useState, useCallback, useEffect } from 'react';
import './CompanyTimelineGrid.css'; // Import CSS for styling
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";

const PhotoSlider = ({ cards }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0); // 1 for right, -1 for left
  const [scrollY, setScrollY] = useState(0); // Track scroll position for parallax

  // Navigate to the next card
  const goToNextCard = useCallback(() => {
    setDirection(1);
    setCurrentIndex((prevIndex) => (prevIndex === cards.length - 1 ? 0 : prevIndex + 1));
  }, [cards.length]);

  // Define animations
  const animationControls = useAnimation();
  const [ref, inView] = useInView();

  // Animate when the element comes into view
  useEffect(() => {
    if (inView) {
      animationControls.start({
        opacity: 1,
        y: 0,
        transition: { duration: 1 },
      });
    }
  }, [animationControls, inView]);

  // Automatically change to the next card after 10 seconds
  useEffect(() => {
    const timer = setTimeout(goToNextCard, 60000); 
    return () => clearTimeout(timer);
  }, [currentIndex, goToNextCard]);

  const handleDotClick = (index) => {
    if (index !== currentIndex) {
      setDirection(index > currentIndex ? 1 : -1); // Determine the direction
      setCurrentIndex(index);
    }
  };

  // Parallax effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY); // Track scroll position
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className='PhotoSlider-container'>
      {/* Stationary Dots */}
      <div className="PhotoSlider-dots">
        {cards.map((card, index) => (
          <div key={index} className="PhotoSlider-YearContainer">
            {index === currentIndex && (
              <div className="PhotoSlider-dot-year">{card.Year}</div>
            )}
            <div
              className={`PhotoSlider-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => handleDotClick(index)}
            >
              {index === currentIndex && (
                <div className="PhotoSlider-dot-year">{card.Year}</div>
              )}
              <div className="PhotoSlider-dot-inner" />
            </div>
          </div>
        ))}
      </div>

      {/* Animated Content and Background */}
      <motion.div
        className="PhotoSlider-slider"
        ref={ref}
        initial={{ opacity: 0, y: 60 }}
        animate={animationControls}
      >
        <motion.div
          className="PhotoSlider-background"
          style={{ 
            backgroundImage: `url(${cards[currentIndex].image})`,
            backgroundPositionY: `${scrollY * 0}px` // Parallax effect on scroll - Adjust the value to change intensity
          }}
          initial={{ x: direction === 1 ? 800 : -800, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{
            x: { 
              type: 'spring', 
              stiffness: 50, 
              damping: 20, 
              duration: 1.5 
            },
            opacity: { duration: 0.8, ease: [0.42, 0, 0.58, 1] },
          }}
          key={currentIndex}
        >
          <div className="PhotoSlider-content">
            <div className="PhotoSlider-text">
              <h2 className="PhotoSlider-heading">{cards[currentIndex].heading}</h2>
              <p className="PhotoSlider-description">{cards[currentIndex].description}</p>
            </div>
            <div className="PhotoSlider-year">
              <h1 className="PhotoSlider-yearText">{cards[currentIndex].Year}</h1>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PhotoSlider;
